import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAdminEnrollment,
  approveEnrollment,
  cancelEnrollment,
  markAttended,
  markNoShow,
  resendEmail,
  promoteFromWaitlist,
} from '../../api/enrollments';
import StatusBadge from '../../components/StatusBadge';
import type { EnrollmentStatus } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

type EmailType = 'confirmation' | 'join_link' | 'reminder' | 'reminder_day_before';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '1rem',
        padding: '0.625rem 0',
        borderBottom: '1px solid var(--gray-100)',
      }}
    >
      <span
        style={{
          minWidth: '180px',
          fontSize: '0.875rem',
          color: 'var(--gray-500)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: '0.9375rem', color: 'var(--gray-800)' }}>{value ?? '—'}</span>
    </div>
  );
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—';
  return format(new Date(dateStr), "d MMM yyyy 'alle' HH:mm", { locale: it });
}

export default function AdminEnrollmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedEmailType, setSelectedEmailType] = useState<EmailType>('confirmation');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const { data: enrollment, isLoading } = useQuery({
    queryKey: ['admin-enrollment', id],
    queryFn: () => getAdminEnrollment(id!),
    enabled: !!id,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-enrollment', id] });
  };

  const handleAction = async (
    action: () => Promise<void>,
    successMsg: string
  ) => {
    setActionError(null);
    setActionSuccess(null);
    try {
      await action();
      setActionSuccess(successMsg);
      invalidate();
    } catch (err: any) {
      setActionError(err?.response?.data?.error ?? 'Errore nell\'operazione');
    }
  };

  const status = enrollment?.status as EnrollmentStatus;

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div>
        <div className="alert alert-danger">Iscrizione non trovata</div>
        <button onClick={() => navigate('/admin/enrollments')} className="btn btn-secondary">
          Torna alle iscrizioni
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => navigate('/admin/enrollments')}
            className="btn btn-ghost btn-sm"
          >
            ← Indietro
          </button>
          <div>
            <h1 className="page-title" style={{ fontSize: '1.25rem' }}>
              {enrollment.user?.nome} {enrollment.user?.cognome}
            </h1>
            <p style={{ color: 'var(--gray-500)', margin: 0, fontSize: '0.875rem' }}>
              {enrollment.user?.email}
            </p>
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Alerts */}
      {actionError && (
        <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
          {actionError}
        </div>
      )}
      {actionSuccess && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          {actionSuccess}
        </div>
      )}

      {/* Action buttons */}
      <div
        className="card"
        style={{ marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}
      >
        <strong style={{ width: '100%', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
          Azioni disponibili:
        </strong>

        {status === 'waitlisted' && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() =>
              handleAction(() => promoteFromWaitlist(id!), 'Utente promosso dalla lista d\'attesa')
            }
          >
            ↑ Promuovi dalla lista d'attesa
          </button>
        )}

        {status === 'zoom_registered' && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() =>
              handleAction(() => approveEnrollment(id!), 'Iscrizione approvata con successo')
            }
          >
            ✓ Approva partecipazione
          </button>
        )}

        {!['cancelled', 'attended'].includes(status) && (
          <button
            className="btn btn-danger btn-sm"
            onClick={() => {
              if (window.confirm('Sei sicuro di voler annullare questa iscrizione?')) {
                handleAction(() => cancelEnrollment(id!), 'Iscrizione annullata');
              }
            }}
          >
            Annulla iscrizione
          </button>
        )}

        {['join_link_sent', 'zoom_approved'].includes(status) && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() =>
              handleAction(() => markAttended(id!), 'Partecipazione confermata')
            }
          >
            ✓ Segna come presente
          </button>
        )}

        {['join_link_sent', 'zoom_approved'].includes(status) && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() =>
              handleAction(() => markNoShow(id!), 'Segnato come non presente')
            }
          >
            ✗ Segna come assente
          </button>
        )}

        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setShowResendModal(true)}
        >
          📧 Reinvia email
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* User info */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Dati partecipante</h3>
          </div>
          <InfoRow label="Nome" value={`${enrollment.user?.nome} ${enrollment.user?.cognome}`} />
          <InfoRow label="Email" value={enrollment.user?.email} />
          <InfoRow label="Azienda" value={enrollment.user?.azienda} />
          <InfoRow
            label="Iscritto il"
            value={formatDate(enrollment.registeredAt)}
          />
        </div>

        {/* Enrollment info */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Dettagli iscrizione</h3>
          </div>
          <InfoRow label="Corso" value={enrollment.edition?.course?.name} />
          <InfoRow label="Edizione" value={enrollment.edition?.label} />
          <InfoRow
            label="Data sessione"
            value={formatDate(enrollment.edition?.scheduledAt)}
          />
          <InfoRow label="Stato" value={<StatusBadge status={status} />} />
          <InfoRow label="ID Iscrizione" value={<code style={{ fontSize: '0.75rem' }}>{enrollment.id}</code>} />
        </div>

        {/* LMS info */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Dati LMS</h3>
          </div>
          <InfoRow label="LMS User ID" value={enrollment.lmsUserId} />
          <InfoRow label="LMS Enrollment ID" value={enrollment.lmsEnrollmentId} />
          <InfoRow label="Iscritto su LMS il" value={formatDate(enrollment.lmsEnrolledAt)} />
        </div>

        {/* Zoom info */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Dati Zoom</h3>
          </div>
          <InfoRow label="Zoom Registrant ID" value={enrollment.zoomRegistrantId} />
          <InfoRow label="Zoom Status" value={enrollment.zoomStatus} />
          <InfoRow label="Registrato su Zoom il" value={formatDate(enrollment.zoomRegisteredAt)} />
          <InfoRow label="Approvato il" value={formatDate(enrollment.zoomApprovedAt)} />
          {enrollment.zoomJoinUrl && (
            <InfoRow
              label="Join URL"
              value={
                <a
                  href={enrollment.zoomJoinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-sm"
                >
                  Apri link Zoom
                </a>
              }
            />
          )}
        </div>

        {/* Email tracking */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Email inviate</h3>
          </div>
          <InfoRow label="Conferma" value={formatDate(enrollment.confirmationSentAt)} />
          <InfoRow label="Link accesso" value={formatDate(enrollment.joinLinkSentAt)} />
          <InfoRow label="Promemoria (3gg prima)" value={formatDate(enrollment.reminderSentAt)} />
          <InfoRow label="Promemoria (1gg prima)" value={formatDate(enrollment.reminderDayBeforeSentAt)} />
        </div>
      </div>

      {/* Extra registration data */}
      {enrollment.extraData && Object.keys(enrollment.extraData).length > 0 && (() => {
        const fields = enrollment.edition?.course?.registrationFields ?? [];
        const labelMap: Record<string, string> = {};
        for (const f of fields) labelMap[f.key] = f.label;
        return (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header">
              <h3 className="card-title">Dati aggiuntivi di iscrizione</h3>
            </div>
            {Object.entries(enrollment.extraData).map(([key, value]) => (
              <InfoRow
                key={key}
                label={labelMap[key] ?? key}
                value={
                  typeof value === 'boolean'
                    ? (value ? '✓ Sì' : '✗ No')
                    : String(value ?? '—')
                }
              />
            ))}
          </div>
        );
      })()}

      {/* Event log */}
      {enrollment.events && enrollment.events.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Cronologia eventi</h3>
          </div>
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Da</th>
                  <th>A</th>
                  <th>Trigger</th>
                  <th>Metadati</th>
                </tr>
              </thead>
              <tbody>
                {enrollment.events.map((event) => (
                  <tr key={event.id}>
                    <td style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                      {formatDate(event.createdAt)}
                    </td>
                    <td>
                      {event.fromStatus ? (
                        <StatusBadge status={event.fromStatus as EnrollmentStatus} />
                      ) : (
                        <span style={{ color: 'var(--gray-400)', fontSize: '0.875rem' }}>—</span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={event.toStatus as EnrollmentStatus} />
                    </td>
                    <td>
                      <code style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                        {event.trigger}
                      </code>
                    </td>
                    <td>
                      {event.metadata ? (
                        <details>
                          <summary
                            style={{
                              cursor: 'pointer',
                              fontSize: '0.8125rem',
                              color: 'var(--primary-light)',
                            }}
                          >
                            Mostra
                          </summary>
                          <pre
                            style={{
                              fontSize: '0.75rem',
                              marginTop: '0.5rem',
                              background: 'var(--gray-50)',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              overflow: 'auto',
                              maxWidth: '300px',
                            }}
                          >
                            {JSON.stringify(event.metadata, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resend Email Modal */}
      {showResendModal && (
        <div className="modal-overlay" onClick={() => setShowResendModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Reinvia email</h2>

            <div className="form-group">
              <label className="form-label">Tipo di email</label>
              <select
                className="form-control"
                value={selectedEmailType}
                onChange={(e) => setSelectedEmailType(e.target.value as EmailType)}
              >
                <option value="confirmation">Email di conferma iscrizione</option>
                <option value="join_link">Email con link di accesso</option>
                <option value="reminder">Email promemoria (3gg prima)</option>
                <option value="reminder_day_before">Email promemoria (1gg prima)</option>
              </select>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => setShowResendModal(false)}
              >
                Annulla
              </button>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  setShowResendModal(false);
                  await handleAction(
                    () => resendEmail(id!, selectedEmailType),
                    `Email "${selectedEmailType}" reinviata con successo`
                  );
                }}
              >
                Invia email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
